/**
 * Alert utility functions for determining available metrics based on device capabilities
 */

/**
 * Get device type based on device properties
 * @param device Device object
 * @returns Device type string
 */
export function getDeviceType(device: any): 'power_meter' | 'th_sensor' | 'presence_sensor' | 'door_sensor' | 'motion_sensor' | 'curtain' | 'blind' | 'hvac' | 'light' | 'switch' | 'unknown' {
  const deviceType = device.type?.toUpperCase() || '';
  // Traits can be at root level or inside capabilities
  let traits = device.traits || [];
  if ((!traits || traits.length === 0) && Array.isArray(device.capabilities)) {
    for (const cap of device.capabilities) {
      if (cap.traits?.length) {
        traits = [...traits, ...cap.traits];
      }
    }
  }
  const sensorProfile = device.attributes?.sensorProfile || {};
  const capabilities = device.capabilities || [];

  // Check for power meter capabilities
  if (sensorProfile.watt !== undefined || sensorProfile.watt_hour !== undefined ||
      sensorProfile.voltage !== undefined || sensorProfile.current !== undefined ||
      device.watt !== undefined || device.wattHour !== undefined ||
      device.voltage !== undefined || device.current !== undefined) {
    // Could be power meter, light with meter, or switch with meter
    if (traits.includes('OnOff') && (traits.includes('Brightness') || capabilities.includes('light'))) {
      return 'light';
    }
    if (traits.includes('OnOff') && !traits.includes('Brightness')) {
      return 'switch';
    }
    return 'power_meter';
  }

  // Check for TH sensor
  if (sensorProfile.temp !== undefined || sensorProfile.rh !== undefined ||
      device.temperature !== undefined || device.humidity !== undefined) {
    // Check if it's HVAC with sensors
    if (deviceType.includes('AC') || deviceType.includes('FAN') || deviceType.includes('HVAC') ||
        traits.includes('Thermostat') || capabilities.includes('hvac')) {
      return 'hvac';
    }
    return 'th_sensor';
  }

  // Check for presence sensor
  if (traits.includes('Occupancy') || traits.includes('OccupancySensing') ||
      sensorProfile.presence !== undefined || device.presence !== undefined ||
      device.occupancy !== undefined || capabilities.includes('presenceSensor')) {
    return 'presence_sensor';
  }

  // Check for door/contact sensor
  // Check traits, deviceType, and detectorEvents for door sensors
  // detectorEvents can be in device.attributes or device.capabilities[n].attributes
  let detectorEventsForType = device.attributes?.detectorEvents || [];
  if ((!detectorEventsForType || !detectorEventsForType.length) && Array.isArray(device.capabilities)) {
    for (const cap of device.capabilities) {
      if (cap.attributes?.detectorEvents?.length) {
        detectorEventsForType = cap.attributes.detectorEvents;
        break;
      }
    }
  }
  const hasDoorEventsForType = Array.isArray(detectorEventsForType) && 
    (detectorEventsForType.includes('opened') || detectorEventsForType.includes('closed'));
  
  if (traits.includes('OpenClose') || traits.includes('ContactSensor') ||
      (traits.includes('Detector') && hasDoorEventsForType) ||
      device.deviceType === 'door_sensor' || device.deviceType === 'contact_sensor' ||
      deviceType.includes('DOOR') || deviceType.includes('CONTACT') ||
      capabilities.includes('doorSensor') || capabilities.includes('contactSensor') ||
      device.isOpen !== undefined) {
    return 'door_sensor';
  }

  // Check for motion sensor
  if (traits.includes('MotionSensor') || traits.includes('Motion') ||
      device.deviceType === 'motion_sensor' || deviceType.includes('MOTION') ||
      capabilities.includes('motionSensor') || device.motionDetected !== undefined) {
    return 'motion_sensor';
  }

  // Check for curtain devices (窗簾)
  if (device.deviceType === 'curtain' || deviceType === 'CURTAIN' ||
      capabilities.includes('curtain')) {
    return 'curtain';
  }

  // Check for blind devices (捲簾)
  if (device.deviceType === 'blind' ||
      deviceType.includes('BLIND') || deviceType.includes('SHADE') ||
      capabilities.includes('blind') ||
      (traits.includes('OpenClose') && device.openPercent !== undefined)) {
    return 'blind';
  }

  // Check for HVAC by type
  if (deviceType.includes('AC') || deviceType.includes('FAN') || deviceType.includes('HVAC') ||
      traits.includes('Thermostat') || capabilities.includes('hvac')) {
    return 'hvac';
  }

  // Check for light by traits
  if (traits.includes('OnOff') && (traits.includes('Brightness') || capabilities.includes('light'))) {
    return 'light';
  }

  // Check for switch by traits
  if (traits.includes('OnOff') && !traits.includes('Brightness')) {
    return 'switch';
  }

  return 'unknown';
}

/**
 * Check if device has a specific metric capability
 * @param device Device object
 * @param metric Metric name
 * @returns True if device supports the metric
 */
export function hasMetricCapability(device: any, metric: string): boolean {
  const sensorProfile = device.attributes?.sensorProfile || {};
  // Traits can be at root level or inside capabilities
  let traits = device.traits || [];
  if ((!traits || traits.length === 0) && Array.isArray(device.capabilities)) {
    for (const cap of device.capabilities) {
      if (cap.traits?.length) {
        traits = [...traits, ...cap.traits];
      }
    }
  }
  const capabilities = device.capabilities || [];

  switch (metric) {
    case 'power':
      return sensorProfile.watt !== undefined || device.watt !== undefined;
    case 'energy':
      return sensorProfile.watt_hour !== undefined || device.wattHour !== undefined;
    case 'voltage':
      return sensorProfile.voltage !== undefined || device.voltage !== undefined;
    case 'current':
      return sensorProfile.current !== undefined || device.current !== undefined;
    case 'temperature':
      return sensorProfile.temp !== undefined || device.temperature !== undefined;
    case 'humidity':
      return sensorProfile.rh !== undefined || device.humidity !== undefined;
    case 'presence':
      return traits.includes('Occupancy') || traits.includes('OccupancySensing') ||
             sensorProfile.presence !== undefined || device.presence !== undefined ||
             device.occupancy !== undefined || capabilities.includes('presenceSensor');
    case 'battery':
      return device.battery !== undefined || sensorProfile.battery !== undefined ||
             device.batteryLevel !== undefined || device.hasBattery === true;
    case 'lux':
    case 'illuminance':
      return sensorProfile.lux !== undefined || 
             traits.includes('Illuminance') || 
             traits.includes('IlluminanceMeasurement') ||
             device.illuminance !== undefined || 
             device.lux !== undefined;
    default:
      return false;
  }
}

/**
 * Get available metrics for a device based on its type, traits, and attributes
 * @param device Device object
 * @returns Array of available metric names
 */
export function getAvailableMetrics(device: any): string[] {
  if (!device) {
    return [];
  }

  const metrics: string[] = [];
  const sensorProfile = device.attributes?.sensorProfile || {};
  // Traits can be at root level or inside capabilities
  let traits = device.traits || [];
  if ((!traits || traits.length === 0) && Array.isArray(device.capabilities)) {
    for (const cap of device.capabilities) {
      if (cap.traits?.length) {
        traits = [...traits, ...cap.traits];
      }
    }
  }
  const capabilities = device.capabilities || [];
  const deviceType = getDeviceType(device);

  // Power metrics (watt, wattHour, voltage, current)
  if (sensorProfile.watt !== undefined || sensorProfile.watt_hour !== undefined ||
      sensorProfile.voltage !== undefined || sensorProfile.current !== undefined ||
      device.watt !== undefined || device.wattHour !== undefined ||
      device.voltage !== undefined || device.current !== undefined) {
    if (sensorProfile.watt !== undefined || device.watt !== undefined) {
      metrics.push('power');
    }
    if (sensorProfile.watt_hour !== undefined || device.wattHour !== undefined) {
      metrics.push('energy');
    }
    if (sensorProfile.voltage !== undefined || device.voltage !== undefined) {
      metrics.push('voltage');
    }
    if (sensorProfile.current !== undefined || device.current !== undefined) {
      metrics.push('current');
    }
  }

  // Temperature/Humidity
  if (sensorProfile.temp !== undefined || sensorProfile.rh !== undefined ||
      device.temperature !== undefined || device.humidity !== undefined) {
    if (sensorProfile.temp !== undefined || device.temperature !== undefined) {
      metrics.push('temperature');
    }
    if (sensorProfile.rh !== undefined || device.humidity !== undefined) {
      metrics.push('humidity');
    }
  }

  // Presence
  if (traits.includes('Occupancy') || traits.includes('OccupancySensing') ||
      sensorProfile.presence !== undefined || device.presence !== undefined ||
      device.occupancy !== undefined || capabilities.includes('presenceSensor')) {
    metrics.push('presence');
  }

  // Door/Contact sensor
  // Check traits, deviceType, and detectorEvents for door sensors
  // detectorEvents can be in device.attributes or device.capabilities[n].attributes
  let detectorEvents = device.attributes?.detectorEvents || [];
  if (!detectorEvents.length && Array.isArray(device.capabilities)) {
    for (const cap of device.capabilities) {
      if (cap.attributes?.detectorEvents?.length) {
        detectorEvents = cap.attributes.detectorEvents;
        break;
      }
    }
  }
  const hasDoorEvents = Array.isArray(detectorEvents) && 
    (detectorEvents.includes('opened') || detectorEvents.includes('closed'));
  
  if (traits.includes('OpenClose') || traits.includes('ContactSensor') ||
      (traits.includes('Detector') && hasDoorEvents) ||
      device.deviceType === 'door_sensor' || device.deviceType === 'contact_sensor' ||
      capabilities.includes('doorSensor') || capabilities.includes('contactSensor') ||
      device.isOpen !== undefined) {
    metrics.push('door_state');
  }

  // Motion sensor
  if (traits.includes('MotionSensor') || traits.includes('Motion') ||
      device.deviceType === 'motion_sensor' ||
      capabilities.includes('motionSensor') || device.motionDetected !== undefined) {
    metrics.push('motion');
  }

  // Lux/Illuminance
  if (sensorProfile.lux !== undefined ||
      traits.includes('Illuminance') || 
      traits.includes('IlluminanceMeasurement') ||
      device.illuminance !== undefined || 
      device.lux !== undefined) {
    metrics.push('lux');
  }

  // Curtain/Blind open percentage
  if (deviceType === 'curtain' || deviceType === 'blind' ||
      traits.includes('OpenClose') || device.openPercent !== undefined) {
    metrics.push('open_percent');
    metrics.push('open_state');
  }

  // State-based metrics (for devices with OnOff capability)
  if (traits.includes('OnOff') || deviceType === 'light' || 
      deviceType === 'switch' || deviceType === 'hvac') {
    metrics.push('power_state'); // 監控開關狀態
  }

  // Online status (all devices support this)
  metrics.push('online'); // 監控設備離線

  // Battery (all devices may have this)
  if (device.battery !== undefined || sensorProfile.battery !== undefined ||
      device.batteryLevel !== undefined || device.hasBattery === true) {
    metrics.push('battery');
  }

  // Remove duplicates and return
  return Array.from(new Set(metrics));
}

/**
 * Get default metric for a device based on its type
 * @param device Device object
 * @returns Default metric name or null
 */
export function getDefaultMetric(device: any): string | null {
  const availableMetrics = getAvailableMetrics(device);
  if (availableMetrics.length === 0) {
    return null;
  }

  const deviceType = getDeviceType(device);

  // Priority order based on device type
  switch (deviceType) {
    case 'power_meter':
      // Prefer power, then energy, then voltage, then current
      return availableMetrics.find(m => m === 'power') ||
             availableMetrics.find(m => m === 'energy') ||
             availableMetrics.find(m => m === 'voltage') ||
             availableMetrics.find(m => m === 'current') ||
             availableMetrics[0];
    case 'th_sensor':
      // Prefer temperature, then humidity, then lux (if available)
      return availableMetrics.find(m => m === 'temperature') ||
             availableMetrics.find(m => m === 'humidity') ||
             availableMetrics.find(m => m === 'lux') ||
             availableMetrics[0];
    case 'presence_sensor':
      // Prefer presence, then lux (if available)
      return availableMetrics.find(m => m === 'presence') ||
             availableMetrics.find(m => m === 'lux') ||
             availableMetrics[0];
    case 'door_sensor':
      // Prefer door_state
      return availableMetrics.find(m => m === 'door_state') ||
             availableMetrics.find(m => m === 'battery') ||
             availableMetrics[0];
    case 'motion_sensor':
      // Prefer motion
      return availableMetrics.find(m => m === 'motion') ||
             availableMetrics.find(m => m === 'battery') ||
             availableMetrics[0];
    case 'hvac':
      // Prefer temperature, then humidity, then lux (if available), then power_state
      return availableMetrics.find(m => m === 'temperature') ||
             availableMetrics.find(m => m === 'humidity') ||
             availableMetrics.find(m => m === 'lux') ||
             availableMetrics.find(m => m === 'power_state') ||
             availableMetrics[0];
    case 'light':
    case 'switch':
      // If has meter, prefer power metrics, otherwise prefer power_state
      if (availableMetrics.includes('power')) {
        return 'power';
      }
      if (availableMetrics.includes('energy')) {
        return 'energy';
      }
      if (availableMetrics.includes('power_state')) {
        return 'power_state';
      }
      return availableMetrics[0];
    case 'curtain':
    case 'blind':
      // Prefer open_percent, then open_state
      return availableMetrics.find(m => m === 'open_percent') ||
             availableMetrics.find(m => m === 'open_state') ||
             availableMetrics[0];
    default:
      return availableMetrics[0];
  }
}

/**
 * Check if a metric is a state-based metric (doesn't require threshold)
 * @param metric Metric name
 * @returns True if metric is state-based
 */
export function isStateMetric(metric: string): boolean {
  return ['presence', 'power_state', 'online', 'door_state', 'motion'].includes(metric);
}

/**
 * Get available conditions for a metric based on its type
 * @param metric Metric name
 * @returns Array of available condition names
 */
export function getAvailableConditions(metric: string): string[] {
  switch (metric) {
    case 'presence':
      return ['detected', 'not_detected'];
    case 'door_state':
      return ['opened', 'closed', 'changed'];
    case 'motion':
      return ['detected', 'not_detected'];
    case 'power_state':
      return ['on', 'off', 'changed'];
    case 'online':
      return ['offline', 'online', 'changed'];
    case 'temperature':
    case 'humidity':
    case 'voltage':
    case 'current':
    case 'power':
    case 'energy':
    case 'battery':
    case 'lux':
    case 'illuminance':
      return ['above', 'below', 'equals', '>=', '<='];
    default:
      return ['above', 'below', 'equals', '>=', '<='];
  }
}

